import React from "react";
import { cn } from "../../styles";

export type SkeletonVariant =
  | "text"
  | "body1"
  | "body2"
  | "subtitle1"
  | "subtitle2"
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | "caption"
  | "overline"
  | "rect"
  | "circular";

export interface SkeletonProps {
  /**
   * Additional class name(s) to apply to the skeleton element.
   */
  className?: string;

  /**
   * Inline style object to apply to the skeleton element.
   */
  style?: React.CSSProperties;

  /**
   * The shape/size variant of the skeleton.
   * Typography variants (h1-h6, body1, caption) have preset dimensions.
   * @default "text"
   */
  variant?: SkeletonVariant;

  /**
   * Number of skeleton rows to render.
   * When > 1, renders multiple stacked skeletons with the last one at default width.
   */
  rows?: number;

  /**
   * Adds bottom margin spacing between skeleton elements.
   * @default false
   */
  gutterBottom?: boolean;

  /**
   * Makes the skeleton span full width of its container.
   * @default false
   */
  fullWidth?: boolean;

  /**
   * Width in pixels. Overrides variant default width.
   */
  width?: number;

  /**
   * Height in pixels. Overrides variant default height.
   */
  height?: number;
}

const variantClassNames: Record<SkeletonVariant, string> = {
  text: "h-4 w-[150px]",
  body1: "h-5 w-[200px]",
  body2: "h-[1.1rem] w-[180px]",
  subtitle1: "h-5 w-[160px]",
  subtitle2: "h-4 w-[140px]",
  h1: "h-12 w-[650px]",
  h2: "h-12 w-[420px]",
  h3: "h-8 w-[400px]",
  h4: "h-8 w-[340px]",
  h5: "h-8 w-[280px]",
  h6: "h-6 w-[220px]",
  caption: "h-3 w-[120px]",
  overline: "h-[0.7rem] w-[100px]",
  rect: "h-[150px] w-[150px]",
  circular: "h-8 w-8",
};

const variantMargins: Partial<Record<SkeletonVariant, string>> = {
  h1: "mb-4",
  h2: "mb-3",
  h3: "mb-3",
  h4: "mb-2",
  h5: "mb-2",
  h6: "mb-2",
  body1: "mb-4",
  body2: "mb-4",
};

export const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(
  (
    {
      className,
      style,
      variant = "text",
      rows,
      gutterBottom = false,
      fullWidth = false,
      width,
      height,
    },
    ref
  ) => {
    const isCircular = variant === "circular";
    const variantClasses = variantClassNames[variant];
    const variantMargin = variantMargins[variant];

    const baseStyle: React.CSSProperties = {
      ...(height && { height: `${height}px` }),
      ...(width && { width: `${width}px` }),
      ...style,
    };

    const skeletonClasses = cn(
      "animate-pulse bg-current opacity-10 dark:opacity-20 [filter:opacity(0.2)]",
      variantClasses,
      isCircular ? "rounded-full inline-block" : variant === "rect" ? "rounded-[2px]" : "rounded",
      fullWidth && "!w-full",
      gutterBottom ? "mb-2" : variantMargin,
      className
    );

    if (!rows || rows < 2) {
      return <div ref={ref} className={skeletonClasses} style={baseStyle} />;
    }

    return (
      <>
        {[...Array(rows - 1).keys()].map((idx) => (
          <div
            key={idx}
            className={cn(skeletonClasses, "mb-2 !w-full")}
            style={baseStyle}
          />
        ))}
        <div ref={ref} className={skeletonClasses} style={baseStyle} />
      </>
    );
  }
);

Skeleton.displayName = "Skeleton";
